import React, { useEffect, useRef } from 'react'
import { observer } from 'mobx-react-lite'
import { number, object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { StyleSheet, ViewProps } from 'react-native'
import { Draft, WorkspaceData } from 'zens-store'
import { Form, NavigationConfirm, WorkspaceType, FormProps } from '@dms/component'
import { FloorSelect } from 'zens-fragment'

const styles = StyleSheet.create({
  general: {
    zIndex: 500,
  },
  location: {
    zIndex: 400,
  },
  floor: {
    zIndex: 200,
  },
})

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  data: WorkspaceData
  onSave?: (draft: Draft<WorkspaceData>) => Promise<void>
}
const WorkspaceAddForm = (props: Props) => {
  const { companyId, data, onSave, ...otherProps } = props
  const { t } = useTranslation()

  const draft = useRef(new Draft<WorkspaceData>(data)).current

  useEffect(() => {
    Object.keys(data).forEach((key) => draft.update(key, data[key as keyof WorkspaceData]))
  }, [data])

  const config = {
    groups: [
      {
        label: t('General information'),
        fields: [
          {
            key: 'name',
            label: t('Workspace name'),
            fieldProps: {
              testID: 'WorkspaceAddForm.Name',
            },
          },
          {
            key: 'workspace_type_id',
            label: t('Workspace type'),
            FieldInputComponent: WorkspaceType,
            fieldProps: { companyId },
          },
        ],
        style: styles.general,
      },
      {
        label: t('Product information'),
        fields: [
          {
            key: 'vendor',
            label: t('Vendor'),
            optional: true,
          },
          {
            key: 'article_number',
            label: t('Article number'),
            optional: true,
          },
        ],
      },
      {
        label: t('Location'),
        fields: [
          {
            key: 'floor_id',
            label: t('Select floor'),
            FieldInputComponent: FloorSelect,
            fieldProps: { companyId, groupBuilding: true },
            style: styles.floor,
          },
        ],
        style: styles.location,
      },
    ],
  }
  const schema = object({
    name: string().required(t('Workspace name is required')),
    workspace_type_id: string().required(t('Workspace type is required')),
    floor_id: number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required(t('Floor is required')),
  })

  return (
    <>
      <Form
        draft={draft}
        config={config}
        schema={schema}
        buttons={[
          [
            {
              title: t('Save'),
              onPress: onSave,
              validate: true,
            },
            {
              title: t('Cancel'),
              href: `/companies/${companyId}/workspaces`,
              light: true,
            },
          ],
        ]}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {}

const Skeleton = (props: SkeletonProps) => {
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        label: t('General information'),
        fields: [
          {
            key: 'name',
            label: t('Workspace name'),
          },
          {
            key: 'workspace_type_id',
            label: t('Workspace type'),
          },
        ],
        style: styles.general,
      },
      {
        label: t('Product information'),
        fields: [
          {
            key: 'vendor',
            label: t('Vendor'),
          },
          {
            key: 'article_number',
            label: t('Article number'),
          },
        ],
      },
      {
        label: t('Location'),
        fields: [
          {
            key: 'floor_id',
            label: t('Select floor'),
            style: styles.floor,
          },
        ],
        style: styles.location,
      },
    ],
  }

  return <Form.Skeleton config={config} numberOfbuttons={2} {...props} />
}
WorkspaceAddForm.Skeleton = Skeleton

export default observer(WorkspaceAddForm)
